/**
 * A wrapper for the `requestIdleCallback` function that provides a fallback for environments
 * where `requestIdleCallback` is not available. It allows for optional repeating of the callback.
 *
 * @param {Function} callback - The function to execute when the browser is idle.
 * @param {Object} [options={}] - Options to configure the callback execution.
 * @param {number} [options.timeout] - A timeout in milliseconds.
 * @param {boolean} [options.repeat=false] - Whether to repeat the callback execution.
 * @param {number} [options.repeatCount=Infinity] - The number of times to repeat the callback.
 * @param {number} [options.pauseTime=0] - The time in milliseconds to wait before executing the callback again.
 * @returns {Object} - An object containing the `executeCallback` method to start the callback and the `cancelIdleCallback` method to cancel it.
 *
 * @example
 * ```js
 * const { executeCallback, cancelIdleCallback } = useRequestIdleCallback(
 *     () => {
 *         console.log('idle');
 *     },
 *     { repeat: true, repeatCount: 3, pauseTime: 1000 },
 * );
 *
 * const id = executeCallback();
 * cancelIdleCallback(id); // to cancel the callback
 * ```
 */
export function useRequestIdleCallback(callback, options = {}) {
  const { pauseTime = 0, repeat = false, repeatCount = Infinity, timeout } = options;

  const idleCallback =
    window.requestIdleCallback ||
    function (cb) {
      return setTimeout(() => {
        const start = Date.now();
        cb({
          didTimeout: false,
          timeRemaining: function () {
            // calculates the remaining time in milliseconds that the browser has to stay idle.
            // It ensures that the remaining idle time for the browser to perform background tasks is at least 0 and at most 50 milliseconds.
            return Math.max(0, 50 - (Date.now() - start));
          },
        });
      }, 1);
    };

  const cancelIdleCallback = window.cancelIdleCallback || clearTimeout;

  function executeCallback() {
    let currentRepeat = 0;

    function runCallback(deadline) {
      callback(deadline);
      currentRepeat += 1;
      if (repeat && currentRepeat < repeatCount) {
        setTimeout(() => {
          idleCallback(runCallback, { timeout });
        }, pauseTime);
      }
    }

    const id = idleCallback(runCallback, { timeout });
    return id;
  }

  return {
    cancelIdleCallback,
    executeCallback,
  };
}
